import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import EditorialMarquee from "@/components/EditorialMarquee";

export default function AllServices() {
  const { services: SERVICES, lang } = useLang();
  return (
    <div data-testid={IDS.services.indexRoot}>
      <Seo
        title="New Brunswick Security Services | Guards & Mobile Patrol"
        description="Compare uniformed guards, mobile patrol, alarm response and event security in New Brunswick. Request a service plan based on your location and hours."
        path="/services"
      />
      <section className="pt-32 md:pt-40 pb-16 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ {lang === "fr" ? "Catalogue · Services" : "Catalogue · Services"} ]
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-4xl md:text-7xl leading-[0.95] tracking-tight max-w-5xl"
          >
            {lang === "fr"
              ? <>Services de sécurité à travers le <i className="text-primary">Nouveau-Brunswick</i></>
              : <>Security services across <i className="text-primary">New Brunswick</i></>}
          </motion.h1>
          <p className="text-foreground/80 mt-8 max-w-2xl text-lg leading-relaxed">
            {lang === "fr"
              ? "Des postes fixes aux visites de patrouille, choisissez la couverture adaptée à votre propriété. Consultez les tâches et confirmez les délais de déploiement pour votre communauté."
              : "From fixed guard posts to patrol visits, choose coverage suited to your property. Review duties and confirm deployment times for your community."}
          </p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-12">
          <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-2">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{
                  duration: 0.6,
                  delay: (i % 2) * 0.08,
                  ease: [0.76, 0, 0.24, 1],
                }}
                className="border-r border-b border-border"
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={IDS.services.card(s.slug)}
                  className="block p-8 md:p-12 group hover:bg-[hsl(0_0%_7%)] transition-colors h-full"
                >
                  <div className="flex justify-between items-start mb-8">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground">
                      {String(i + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}
                    </span>
                    <ArrowUpRight
                      size={20}
                      strokeWidth={1.2}
                      className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                    />
                  </div>
                  <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-4 group-hover:text-primary transition-colors">
                    {s.name}
                  </h2>
                  <p className="text-foreground/70 max-w-xl leading-relaxed">
                    {s.summary}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />
      <QuoteForm />
    </div>
  );
}
